import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { DialogShell, DialogActions } from './ConfirmDialog';
import StorageExceededDialog from './StorageExceededDialog';
import { TITLE_MAX_LENGTH } from './EditVideoDetailsDialog';
import { useVideos, broadcastVideosRefresh } from '../hooks/useVideos';
import { useStorage, type StorageInfo } from '../hooks/useStorage';
import { saveAsMp4ToastId } from '../hooks/useMp4Download';
import { apiClient } from '../lib/apiClient';
import { toast } from '../lib/toast';

/** Same limit as the backend. */
export const MAX_UPLOAD_BYTES = 2 * 1024 ** 3;
const PROBE_TIMEOUT_MS = 10_000;

/** MP4, WebM or MOV (by type, or by name when the browser reports none). */
export const uploadType = (file: File): 'video/mp4' | 'video/webm' | 'video/quicktime' | null => {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  if (type === 'video/mp4' || (!type && name.endsWith('.mp4'))) return 'video/mp4';
  if (type === 'video/webm' || (!type && name.endsWith('.webm'))) return 'video/webm';
  if (type === 'video/quicktime' || name.endsWith('.mov')) return 'video/quicktime';
  return null;
};

/** Length in seconds (0 when unknown) and whether this browser can play the file. */
const probe = (file: Blob) =>
  new Promise<{ duration: number; playable: boolean }>((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    const done = (result: { duration: number; playable: boolean }) => {
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      video.load();
      resolve(result);
    };
    // No answer in time: assume it plays (the server reads the length).
    const timer = window.setTimeout(() => done({ duration: 0, playable: true }), PROBE_TIMEOUT_MS);
    video.preload = 'auto';
    video.muted = true;
    video.onloadeddata = () => done({
      duration: Number.isFinite(video.duration) ? video.duration : 0,
      playable: video.videoWidth > 0,
    });
    video.onerror = () => done({ duration: 0, playable: false });
    video.src = url;
  });

const formatSize = (bytes: number) =>
  bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(1)} GB` : `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;

interface UploadVideoDialogProps {
  file: File;
  onClose: () => void;
}

/**
 * Adds a video file from the computer to My Library: title, upload with progress and Cancel.
 * Files the browser can't play (e.g. MOV, HEVC) are converted to MP4 on the server afterwards.
 */
const UploadVideoDialog: React.FC<UploadVideoDialogProps> = ({ file, onClose }) => {
  const { t } = useTranslation();
  const { uploadVideo } = useVideos();
  const { getStorageInfo } = useStorage();
  const [title, setTitle] = useState(() => file.name.replace(/\.[^.]+$/, '').trim().slice(0, TITLE_MAX_LENGTH));
  const [info, setInfo] = useState<{ duration: number; playable: boolean } | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [storageInfo, setStorageInfo] = useState<StorageInfo | null | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);
  const type = uploadType(file);

  useEffect(() => {
    let alive = true;
    void probe(file).then((r) => { if (alive) setInfo(r); });
    return () => { alive = false; abortRef.current?.abort(); };
  }, [file]);

  const uploading = progress !== null;
  // Everything is sent: the server is storing it, Cancel can no longer stop it.
  const storing = uploading && progress >= 1;
  const needsConversion = type === 'video/quicktime' || info?.playable === false;

  const start = async () => {
    const name = title.trim();
    if (!name || !info || uploading) return;
    const abort = new AbortController();
    abortRef.current = abort;
    setProgress(0);
    const result = await uploadVideo(
      new File([file], file.name, { type: type ?? file.type }),
      name,
      '',
      info.duration > 0 ? info.duration : undefined,
      { onProgress: setProgress, signal: abort.signal },
    );
    abortRef.current = null;
    if (result.status === 'cancelled') {
      toast.info(t('video_upload.cancelled', 'Upload cancelled.'));
      onClose();
      return;
    }
    if (result.status === 'storage_exceeded') {
      setProgress(null);
      setStorageInfo(await getStorageInfo());
      return;
    }
    if (result.status === 'error') {
      setProgress(null);
      toast.error(t('video_upload.failed', 'Could not upload the video. Please try again.'));
      return;
    }
    if (needsConversion) {
      const { error } = await apiClient.startVideoJob(result.video.id, 'mp4', { replace: true });
      if (error) {
        toast.warning(t('video_upload.convert_not_started', 'Uploaded, but the conversion to MP4 could not start. It may not play in this browser.'));
      } else {
        toast.info(t('video_upload.converting', 'Uploaded — converting to MP4 so it plays everywhere…'), { id: saveAsMp4ToastId(result.video.id) });
        broadcastVideosRefresh();
      }
    } else {
      toast.success(t('video_upload.done', 'Video uploaded to My Library.'));
    }
    onClose();
  };

  if (storageInfo !== undefined) {
    return <StorageExceededDialog storageInfo={storageInfo} onClose={onClose} />;
  }

  const muted: React.CSSProperties = { fontSize: 12, color: 'var(--text-secondary, #6b7280)' };

  return (
    <DialogShell title={t('video_upload.title', 'Upload video')} onCancel={onClose} busy={uploading} width={480}>
      <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 600, paddingRight: 20 }}>
        {t('video_upload.title', 'Upload video')}
      </h3>
      <p style={{ ...muted, margin: '0 0 12px', wordBreak: 'break-all' }}>
        {file.name} · {formatSize(file.size)}
        {info && info.duration > 0 && ` · ${Math.floor(info.duration / 60)}:${String(Math.round(info.duration % 60)).padStart(2, '0')}`}
      </p>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary, #6b7280)' }}>
        {t('title_label')}
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={TITLE_MAX_LENGTH}
          disabled={uploading}
          autoFocus
          style={{
            display: 'block', width: '100%', marginTop: 6, boxSizing: 'border-box',
            padding: '8px 10px', borderRadius: 6, fontSize: 14,
            border: '1px solid var(--theme-border, #d1d5db)',
            background: 'var(--input-bg, var(--card-bg, #fff))',
            color: 'var(--text-primary, #111)',
          }}
        />
      </label>
      {needsConversion && !uploading && (
        <p style={{ ...muted, margin: '10px 0 0' }}>
          {t('video_upload.will_convert', 'This file will be converted to MP4 after the upload so it plays everywhere.')}
        </p>
      )}
      {!info && (
        <p style={{ ...muted, margin: '10px 0 0', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Loader2 size={13} className="animate-spin" aria-hidden />
          {t('video_upload.reading', 'Reading the video…')}
        </p>
      )}
      {uploading && (
        <div style={{ marginTop: 14 }}>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            style={{ height: 6, borderRadius: 3, background: 'var(--controlsBg, rgba(0,0,0,0.08))', overflow: 'hidden' }}
          >
            <div style={{ width: `${progress * 100}%`, height: '100%', background: 'var(--theme-primary, #1976d2)', transition: 'width 0.2s' }} />
          </div>
          <p style={{ ...muted, margin: '6px 0 0' }}>
            {storing
              ? t('video_upload.storing', 'Saving to My Library…')
              : t('video_upload.uploading', 'Uploading · {{pct}}%', { pct: Math.round(progress * 100) })}
          </p>
        </div>
      )}
      <DialogActions
        confirmLabel={t('video_upload.upload', 'Upload')}
        cancelLabel={t('cancel', 'Cancel')}
        busy={storing}
        confirmDisabled={!title.trim() || !info || uploading}
        onConfirm={() => void start()}
        onCancel={() => (uploading ? abortRef.current?.abort() : onClose())}
      />
    </DialogShell>
  );
};

export default UploadVideoDialog;
